/**
 * batch-66 verkoop-extractie (FO-493) - tekst-hulpjes voor de deterministische
 * naverwerking van een factuur-PDF. Puur: geen DB, geen AI, geen klok.
 *
 * De tekstlaag komt uit de gebundelde pdf.js (`extraheerPdfTekstCore`). Die zet
 * bij lettertypes met kerning LOSSE SPATIES midden in woorden ("Gewerkt e uren
 * Muham m ed Belek", "Uitgifte datum : 08- 09- 2026", "W B2601580"). Welke
 * spatie echt is, valt uit de tekst zelf niet af te leiden. Daarom werkt alles
 * hier op de GEPLETTE vorm (alle witruimte weg) en wordt de leesbare schrijfwijze
 * achteraf hersteld met een woordenlijst (`herstelSpaties`).
 */

/** Regels van een tekst (LF en CRLF). */
export function splitsRegels(tekst: string): string[] {
  return tekst.split(/\r?\n/)
}

/** Alle witruimte weg (ook harde spaties); hoofdletters blijven staan. */
export function plet(waarde: string): string {
  return waarde.replace(/\s+/g, '')
}

/** Geplet en in kleine letters: de vergelijk-vorm voor labels en kenmerken. */
export function pletKlein(waarde: string): string {
  return plet(waarde).toLowerCase()
}

/** Witruimte samenvoegen tot enkele spaties en trimmen (leesbare vorm). */
export function normaliseerWitruimte(waarde: string): string {
  return waarde.replace(/\s+/g, ' ').trim()
}

/**
 * Vergelijk-sleutel voor een persoonsnaam: geplet, kleine letters, accenten en
 * leestekens weg. "Muham m ed Belek" en "Muhammed Belek" geven dezelfde sleutel.
 */
export function naamSleutel(naam: string | null | undefined): string {
  if (!naam) return ''
  return naam
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Regex-bron die een letterlijke tekst vindt ongeacht losse witruimte tussen de
 * tekens ("Gewerkte uren" vindt ook "Gewerkt e uren"). Witruimte in de invoer
 * zelf telt niet mee.
 */
export function losseRegexBron(letterlijk: string): string {
  return plet(letterlijk)
    .split('')
    .map((teken) => teken.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&'))
    .join('\\s*')
}

/**
 * NL-bedrag naar hele centen: "3.259,56" -> 325956, "-1.210,00" en "1.210,00-"
 * -> -121000. Verwacht precies twee decimalen; anders null (geen gok).
 */
export function parseNlBedragNaarCenten(waarde: string | null | undefined): number | null {
  if (waarde == null) return null
  const kaal = plet(waarde).replace(/€/g, '')
  const negatief = kaal.startsWith('-') || kaal.endsWith('-')
  const cijfers = kaal.replace(/^-|-$/g, '')
  if (!/^\d{1,3}(?:\.\d{3})*,\d{2}$/.test(cijfers) && !/^\d+,\d{2}$/.test(cijfers)) return null
  const [heel, decimaal] = cijfers.split(',')
  const centen = Number.parseInt((heel ?? '').replace(/\./g, ''), 10) * 100 + Number.parseInt(decimaal ?? '0', 10)
  if (!Number.isSafeInteger(centen)) return null
  return negatief && centen !== 0 ? -centen : centen
}

/**
 * NL-getal naar duizendsten (geheel getal): "5,500" -> 5500 (= 5,5), "8" ->
 * 8000, "1.232" -> 1232000 (punt = duizendtal). De komma is ALTIJD het
 * decimaalteken; maximaal drie decimalen, anders null.
 */
export function parseNlGetalNaarDuizendsten(waarde: string | null | undefined): number | null {
  if (waarde == null) return null
  const kaal = plet(waarde)
  const negatief = kaal.startsWith('-')
  const cijfers = negatief ? kaal.slice(1) : kaal
  const m = /^(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{1,3}))?$/.exec(cijfers)
  if (!m) return null
  const heel = Number.parseInt((m[1] ?? '').replace(/\./g, ''), 10)
  const decimaal = Number.parseInt((m[2] ?? '').padEnd(3, '0') || '0', 10)
  const duizendsten = heel * 1000 + decimaal
  if (!Number.isSafeInteger(duizendsten)) return null
  return negatief && duizendsten !== 0 ? -duizendsten : duizendsten
}

/** Centen -> getal met twee decimalen (voor de bestaande number-velden). */
export function centenNaarGetal(centen: number): number {
  return centen / 100
}

/** Duizendsten -> getal (5500 -> 5.5). */
export function duizendstenNaarGetal(duizendsten: number): number {
  return duizendsten / 1000
}

/** Getal -> hele centen (afgerond; float-drift van 0.1 + 0.2 valt weg). */
export function getalNaarCenten(waarde: number): number {
  return Math.round(waarde * 100)
}

/**
 * "dd-mm-jjjj" (ook met losse spaties: "08- 09- 2026") -> "jjjj-mm-dd". Een
 * datum die niet bestaat (31-02) geeft null.
 */
export function parseNlDatum(waarde: string | null | undefined): string | null {
  if (waarde == null) return null
  const m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(plet(waarde))
  if (!m) return null
  const dag = Number.parseInt(m[1] ?? '', 10)
  const maand = Number.parseInt(m[2] ?? '', 10)
  const jaar = Number.parseInt(m[3] ?? '', 10)
  const d = new Date(Date.UTC(jaar, maand - 1, dag))
  if (d.getUTCFullYear() !== jaar || d.getUTCMonth() !== maand - 1 || d.getUTCDate() !== dag) {
    return null
  }
  return `${String(jaar).padStart(4, '0')}-${String(maand).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
}

/** "jjjj-mm-dd" -> "dd-mm-jjjj" (NL-weergave, ook de vorm in de PDF-tekst). */
export function isoNaarNlDatum(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso
}

/** "H:MM" of "HH:MM" -> "HH:MM", of null bij een ongeldige tijd. */
export function normaliseerKlokTijd(waarde: string | null | undefined): string | null {
  if (waarde == null) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(plet(waarde))
  if (!m) return null
  const uur = Number.parseInt(m[1] ?? '', 10)
  const minuut = Number.parseInt(m[2] ?? '', 10)
  if (uur > 23 || minuut > 59) return null
  return `${String(uur).padStart(2, '0')}:${String(minuut).padStart(2, '0')}`
}

/** Tussenvoegsels die als los woord in een naam blijven staan. */
const TUSSENVOEGSELS = new Set([
  'van', 'de', 'der', 'den', 'ten', 'ter', 'te', 'het', 'op', 'in', 'aan', 'bij', 'uit', 'voor',
  'el', 'al', 'la', 'le', 'di', 'da', 'du', 'von', 'zu', 'bin', 'ben',
])

/**
 * Herstel de spaties in een geplette tekst met een woordenlijst (bv. de woorden
 * uit de AI-extractie of bekende medewerkersnamen): zoekt een opdeling waarbij
 * elk deel een bekend woord is. Vergelijkt zonder hoofdletters en accenten en
 * geeft de schrijfwijze uit de woordenlijst terug. Null als er geen volledige
 * opdeling is (dan beslist de aanroeper over een terugval).
 */
export function herstelSpaties(geplet: string, woordenlijst: Iterable<string>): string | null {
  const doel = geplet
  if (doel.length === 0) return ''
  // Te lange invoer: geen kwadratisch werk op vrije tekst uit een open mailbox.
  if (doel.length > 400) return null
  const woorden = new Map<string, string>()
  for (const woord of woordenlijst) {
    const schoon = woord.trim()
    if (schoon.length === 0) continue
    const sleutel = vergelijkVorm(schoon)
    if (sleutel.length > 0 && !woorden.has(sleutel)) woorden.set(sleutel, schoon)
  }
  if (woorden.size === 0) return null
  const doelVorm = vergelijkVorm(doel)
  // vergelijkVorm kan tekens laten vallen (accenten); dan klopt de index niet meer.
  if (doelVorm.length !== doel.length) return null
  const maxLengte = Math.max(...[...woorden.keys()].map((w) => w.length))
  // beste[i] = de opdeling van doel[0..i) met de minste woorden.
  const beste: Array<string[] | null> = new Array<string[] | null>(doel.length + 1).fill(null)
  beste[0] = []
  for (let eind = 1; eind <= doel.length; eind += 1) {
    for (let start = Math.max(0, eind - maxLengte); start < eind; start += 1) {
      const vorige = beste[start]
      if (!vorige) continue
      const woord = woorden.get(doelVorm.slice(start, eind))
      if (woord === undefined) continue
      const huidige = beste[eind]
      if (!huidige || vorige.length + 1 < huidige.length) beste[eind] = [...vorige, woord]
    }
  }
  const uitkomst = beste[doel.length]
  return uitkomst ? uitkomst.join(' ') : null
}

/** Vergelijk-vorm per teken: kleine letters, accenten weg (lengte blijft gelijk waar mogelijk). */
function vergelijkVorm(waarde: string): string {
  return waarde
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
}

/**
 * Terugval zonder woordenlijst voor een PERSOONSNAAM met kerning-spaties: een
 * brokje dat met een kleine letter begint hoort bij het vorige brokje, tenzij
 * het een tussenvoegsel is. "Muham m ed Belek" -> "Muhammed Belek",
 * "Luuk van St iphout" -> "Luuk van Stiphout", "R ico Hendrikx" -> "Rico Hendrikx".
 */
export function ontkernNaam(ruw: string): string {
  const brokken = normaliseerWitruimte(ruw).split(' ').filter((b) => b.length > 0)
  const uit: string[] = []
  for (const brok of brokken) {
    const vorige = uit[uit.length - 1]
    const beginKlein = /^\p{Ll}/u.test(brok)
    const vorigeIsTussenvoegsel = vorige !== undefined && TUSSENVOEGSELS.has(vorige.toLowerCase())
    if (
      vorige !== undefined &&
      beginKlein &&
      !TUSSENVOEGSELS.has(brok.toLowerCase()) &&
      !vorigeIsTussenvoegsel
    ) {
      uit[uit.length - 1] = `${vorige}${brok}`
    } else {
      uit.push(brok)
    }
  }
  return uit.join(' ')
}

/** Losse woorden uit een verzameling teksten (voor `herstelSpaties`). */
export function woordenUit(teksten: Iterable<string | null | undefined>): string[] {
  const uit = new Set<string>()
  for (const tekst of teksten) {
    if (!tekst) continue
    for (const woord of tekst.split(/\s+/)) {
      if (woord.length > 0) uit.add(woord)
    }
  }
  return [...uit]
}
